import { BrowserUtility } from './browser-utility';
import { CommonConstant } from './constants/common';
import { DateFormat, DateUtility } from './date';

import {
  isString,
  isNotEmpty,
  isNotNull,
  isBoolean,
  isValidArray,
  isValidObject,
  isValidNumber,
  isValidDate,
  getValidArray,
  numberWithCommas,
  decimalWithCommas,
  roundNumber,
  getFixNumber,
  copyToClipboard,
  generateRandomId,
  sortArrayObjectsByKey,
  sortDropdownOptions,
  sortObject,
  getCdnLink,
  tryParseJSONObject,
  objectToParams,
  removeUnderscoreAndCapitalize,
  toTitleCase,
  toCapitalize,
  truncateString,
  searchQueryCompare,
  downloadCSV,
  downloadPDF,
  timeoutPromise,
  reorderList,
  removeDuplicatesByKey,
  base64ToBlob,
  getCookie,
  maskAccountNumber,
} from '@skupreme/shared/utils';

type JsonValue = string | number | boolean | null | JsonObject | JsonArray | Date;
type JsonObject = { [key: string]: JsonValue };
type JsonArray = JsonValue[];

/**
 * CommonUtility class - delegates to @skupreme/shared for common functions,
 * keeps warehouse-dashboard specific functions locally
 */
export class CommonUtility {
  // ===== Functions from @skupreme/shared =====

  static isString = isString;
  static isNotEmpty = isNotEmpty;
  static isNotNull = isNotNull;
  static isBoolean = isBoolean;
  static isValidArray = isValidArray;
  static isValidObject = isValidObject;
  static isValidNumber = isValidNumber;
  static isValidDate = isValidDate;
  static getValidArray = getValidArray;
  static numberWithCommas = numberWithCommas;
  static decimalWithCommas = decimalWithCommas;
  static roundNumber = roundNumber;
  static getFixNumber = getFixNumber;
  static copyToClipboard = copyToClipboard;
  static generateRandomId = generateRandomId;
  static sortArrayObjectsByKey = sortArrayObjectsByKey;
  static sortDropdownOptions = sortDropdownOptions;
  static sortObject = sortObject;
  static getCdnLink = getCdnLink;
  static tryParseJSONObject = tryParseJSONObject;
  static objectToParams = objectToParams;
  static removeUnderscoreAndCapitalize = removeUnderscoreAndCapitalize;
  static toTitleCase = toTitleCase;
  static toCapitalize = toCapitalize;
  static truncateString = truncateString;
  static searchQueryCompare = searchQueryCompare;
  static downloadCSV = downloadCSV;
  static downloadPDF = downloadPDF;
  static timeoutPromise = timeoutPromise;
  static reorderList = reorderList;
  static removeDuplicatesByKey = removeDuplicatesByKey;
  static base64ToBlob = base64ToBlob;
  static getCookie = getCookie;
  static maskAccountNumber = maskAccountNumber;

  // ===== Warehouse-dashboard specific functions =====

  static getCurrency(): string {
    const temp = BrowserUtility.get(CommonConstant.currentRegion);
    return temp !== 'undefined' ? temp || 'USD' : 'USD';
  }

  static currencyFormat(
    value: number,
    currency?: string,
    options: Intl.NumberFormatOptions = {},
    decimalPoints = 2,
  ): string {
    const tempCurrency = currency || CommonUtility.getCurrency();
    if (Number.isNaN(value)) return String(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: tempCurrency,
      maximumFractionDigits: decimalPoints,
      ...options,
    }).format(value || 0);
  }

  static isStagingEnv(): boolean {
    return import.meta.env.DEV;
  }

  static isFunction(fn: any): fn is (...args: any[]) => any {
    return typeof fn === 'function';
  }

  static compareUrls(url1: string, url2: string): boolean {
    return url1.replace(/\/+$/, '') === url2.replace(/\/+$/, '');
  }

  static getShortAddress(address: Record<string, any>): string {
    const parts = [
      address.city,
      address.state || address.stateOrRegion,
      address.countryCode,
    ].filter(Boolean);
    return parts.join(', ');
  }

  static getLongAddress(address: Record<string, any>, showEmail = true): string {
    if (!address) return '';
    const parts = [
      address.addressLine1 || address.addressLineOne,
      address.addressLine2 || address.addressLineTwo,
      address.city,
      address.districtOrCounty,
      address.stateOrRegion,
      address.postalCode,
      address.countryCode || address.country,
      address.phoneNumber,
      showEmail ? address.email : undefined,
    ].filter(Boolean);
    return parts.join(', ');
  }

  /**
   * Checks if a value should be considered "empty" and removed
   */
  static isEmpty(value: unknown): boolean {
    // null and undefined are empty
    if (value === null || value === undefined) {
      return true;
    }

    // Empty strings are empty
    if (typeof value === 'string' && value.trim() === '') {
      return true;
    }

    // Empty arrays are empty
    if (Array.isArray(value) && value.length === 0) {
      return true;
    }

    // Empty objects are empty (but not Dates or other special objects)
    if (
      typeof value === 'object' &&
      !(value instanceof Date) &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0
    ) {
      return true;
    }

    return false;
  }

  /**
   * Recursively removes empty fields from an object or array
   * Preserves: numbers (including 0), booleans (including false), Dates, and non-empty values
   * Removes: null, undefined, empty strings, empty arrays, and empty objects
   */
  static removeEmptyFields<T>(data: T): T {
    // Handle null/undefined
    if (data === null || data === undefined) {
      return data;
    }

    // Handle primitives and special objects (Date, etc.)
    if (
      typeof data !== 'object' ||
      data instanceof Date ||
      data instanceof RegExp ||
      data instanceof Map ||
      data instanceof Set
    ) {
      return data;
    }

    // Handle arrays
    if (Array.isArray(data)) {
      const cleaned = data
        .map(item => this.removeEmptyFields(item))
        .filter(item => !this.isEmpty(item));
      return cleaned as T;
    }

    // Handle objects
    const cleaned: JsonObject = {};

    for (const [key, value] of Object.entries(data)) {
      // Recursively clean nested values
      const cleanedValue = this.removeEmptyFields(value);

      // Only add to result if not empty
      if (!this.isEmpty(cleanedValue)) {
        cleaned[key] = cleanedValue;
      }
    }

    return cleaned as T;
  }

  static getSegmentByPosition(url: string, position: number): string | null {
    const segments = url.split('/');
    return position >= 0 && position < segments.length ? segments[position] : null;
  }

  static toCamelCase(str: string): string {
    return str
      .toLowerCase()
      .split(' ')
      .map((word, i) => (i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
      .join('');
  }

  static formatApiKeys(value: any): string {
    if (isBoolean(value)) return value ? 'True' : 'False';
    if (isValidNumber(value)) return String(value);
    if (isString(value)) return toTitleCase(value);
    if (isValidDate(value)) return DateUtility.formatDate(value, DateFormat.dateTime);
    return '';
  }

  static isMobileDevice(): boolean {
    return /Mobi|Android|iPhone|iPad|iPod|Windows Phone|webOS|BlackBerry|BB10|Opera Mini|Silk/i.test(
      navigator.userAgent,
    );
  }
}
